import axios from 'axios';
import api from './api';
import type {
  Policy,
  PolicyCreateRequest,
  PolicyFilters,
  PaginatedResponse,
  RenewalEligibility,
  RenewalRefusal,
  RenewalRefusalCode,
  VehicleCheckResult,
} from '@/types';

const RENEWAL_REFUSAL_CODES: ReadonlySet<string> = new Set<RenewalRefusalCode>([
  'renewal_source_not_found',
  'renewal_source_other_broker',
  'renewal_source_not_issued',
  'renewal_source_already_renewed',
  'renewal_source_installment',
  'renewal_customer_mismatch',
]);

/**
 * B9 -- read a renewal-link refusal off a 400.
 *
 * Two shapes reach the browser:
 * - flat, where the view unwraps `RenewalLinkRefused` (`PolicyViewSet.create`):
 *   `{"previous_policy": ["<message>"], "code": "<refusal code>"}` (key
 *   `previous_policy_id` on the integrations quote submit);
 * - the platform error envelope, everywhere else (e.g. POST /quotes/requests/):
 *   `{"success": false, "error": {"code": "<refusal code>", "message": "..."}}`.
 * Returns null for any other error, so a caller falls through to its normal
 * error handling.
 */
export function renewalRefusalOf(error: unknown): RenewalRefusal | null {
  if (!axios.isAxiosError(error) || error.response?.status !== 400) return null;
  const data = error.response.data as Record<string, unknown> | undefined;
  if (!data || typeof data !== 'object') return null;
  const fallback = 'This policy cannot be linked as a renewal.';

  const flatCode = data.code;
  if (typeof flatCode === 'string' && RENEWAL_REFUSAL_CODES.has(flatCode)) {
    const raw = data.previous_policy ?? data.previous_policy_id;
    const first = Array.isArray(raw) ? raw[0] : raw;
    const message = typeof first === 'string' && first ? first : fallback;
    return { code: flatCode as RenewalRefusalCode, message };
  }

  const envelope = data.error as Record<string, unknown> | undefined;
  if (envelope && typeof envelope === 'object') {
    const code = envelope.code;
    if (typeof code === 'string' && RENEWAL_REFUSAL_CODES.has(code)) {
      const message = typeof envelope.message === 'string' && envelope.message ? envelope.message : fallback;
      return { code: code as RenewalRefusalCode, message };
    }
  }
  return null;
}

// Upfront vehicle lookup (round-2 Wave F2, item 3b) — matched against our own
// issued policies (policies.PolicyVehicle) or customers.CustomerVehicle, NOT
// the (currently unpopulated) vehicles.VehicleRegistration registry.
/** What `/policies/renewal-prefill/` answers with.
 *
 *  `found` is false for a policy with no vehicle row (non-motor, or entered by
 *  hand) — the customer still comes back, and the wizard still opens. Prefill
 *  accelerates the journey; it never gates it. */
export interface RenewalPrefill {
  found: boolean;
  policy_id?: string;
  customer?: {
    id: string;
    full_name: string;
    phone: string;
    email: string;
    date_of_birth: string | null;
    gender: string;
    /** B-02/C1. The proposer's PIN, which the motor wizard's Customer step
     *  requires -- motor is zone-rated on it. Optional because a prefill
     *  served before that field existed does not carry it. */
    pincode?: string | null;
  } | null;
  vehicle?: {
    vehicle_category?: string | null;
    make_id?: string | null;
    make_name?: string | null;
    model_id?: string | null;
    model_name?: string | null;
    variant_id?: string | null;
    variant_name?: string | null;
    fuel_type?: string | null;
    manufacturing_year?: number | null;
    registration_number?: string | null;
    registration_date?: string | null;
    rto_code?: string | null;
    rto_label?: string | null;
  };
  prior_policy?: {
    previous_policy_number?: string | null;
    previous_insurer_id?: string | null;
    previous_insurer_name?: string | null;
    previous_policy_expiry?: string | null;
    previous_ncb?: number | null;
    has_claim_in_previous?: boolean | null;
  };
  /** B9 -- whether the source can be linked as the renewed policy at all.
   *  Absent from an older server; treat absent as "not refused". */
  eligibility?: RenewalEligibility;
}

export interface VehicleLookupResult {
  found: boolean;
  /** Which tier answered. The first three are ours and cost nothing;
   *  'vehicle_registry' is a vehicle already bought from a vendor. */
  source?: 'policy' | 'customer_vehicle' | 'vehicle_registry';
  /** True only when this call reached a vendor and was therefore billed. */
  is_external?: boolean;
  /** The registry has nothing, but a vendor could be asked — for a price. */
  external_available?: boolean;
  verified_at?: string | null;
  is_stale?: boolean;
  recommend_refresh?: boolean;
  needs_variant_choice?: boolean;
  vehicle?: {
    vehicle_category: string | null;
    make_id: string | null;
    make_name: string;
    model_id: string | null;
    model_name: string;
    variant_id: string | null;
    variant_name: string;
    fuel_type: string;
    manufacturing_year: number | null;
    registration_date: string | null;
    rto_code: string | null;
    rto_label: string | null;
    /** Offered when the vendor named a model but no variant — the broker
     *  chooses, because the variant sets the premium. Already ordered
     *  best-first against `variant_hint`; `score` is 0..1. */
    variant_options?: { id: string; name: string; score?: number }[];
    /** The one worth pointing at, when one stands out. NEVER pre-select it:
     *  a suggestion the broker confirms is their decision, a filled-in field
     *  they scrolled past is not, and the variant sets the premium. */
    suggested_variant_id?: string | null;
    /** True when the variant came from a choice a human already made for
     *  these exact vendor strings, rather than from a matching rule. */
    variant_was_learned?: boolean;
    /** The make and model above are APPLIED, not merely proposed — that is
     *  what makes the form fast. These are what they could otherwise have
     *  been, so a wrong guess is one tap to fix rather than a vehicle typed
     *  out by hand. Ordered most-plausible-first. */
    make_options?: { id: string; name: string }[];
    model_options?: { id: string; name: string; make_id?: string; make_name?: string }[];
    variant_hint?: string;
    chassis_number?: string;
    engine_number?: string;
    pincode?: string;
  };
  prior_policy?: {
    /** B9 -- the matched policy's id, so the step can offer "Renew this
     *  policy" against it. Only set when `source === 'policy'`. */
    policy_id?: string | null;
    previous_policy_number: string;
    previous_insurer_id: string | null;
    previous_insurer_name: string;
    previous_policy_expiry: string | null;
    previous_ncb: number | null;
    has_claim_in_previous: boolean;
  };
}

// ── Premium-paying term (Life / Health / Term installments) ────────────────
// One policy, one policy number, many installments. These types are declared
// HERE rather than widened into `types/index.ts`, which is owned elsewhere
// this batch.

/** The four modes the server accepts. Values match `Policy.PAYMENT_FREQUENCY_CHOICES`. */
export type PaymentFrequency = 'monthly' | 'quarterly' | 'half_yearly' | 'annually';

export const PAYMENT_FREQUENCY_OPTIONS: { value: PaymentFrequency; label: string; months: number }[] = [
  { value: 'monthly', label: 'Monthly', months: 1 },
  { value: 'quarterly', label: 'Quarterly', months: 3 },
  { value: 'half_yearly', label: 'Half-Yearly', months: 6 },
  { value: 'annually', label: 'Annually', months: 12 },
];

/** `pending` -> `overdue` -> `paid`. `waived` settles without money;
 *  `cancelled` is a row a shortened term no longer needs. */
export type InstallmentStatus = 'pending' | 'paid' | 'overdue' | 'waived' | 'cancelled';

export interface PolicyInstallment {
  id: string;
  installment_number: number;
  due_date: string;
  period_start: string;
  period_end: string;
  amount: string;
  status: InstallmentStatus;
  status_display: string;
  is_settled: boolean;
  /** Unpaid AND past `grace_until`. This — not a passed expiry date — is what
   *  lapses a policy. */
  is_past_grace: boolean;
  grace_period_days: number;
  grace_until: string;
  /** Which rung answered: 'product' | 'insurer' | 'default'. Shown so a broker
   *  asking "why 30 days?" gets provenance, not a bare number. */
  grace_period_source: string;
  paid_at: string | null;
  paid_amount: string | null;
  payment_reference: string;
  payment_mode: string;
  notes: string;
}

/** The derived numbers above the schedule table. `null` on the policy payload
 *  for a policy that is NOT paid in installments — which is a different claim
 *  from "on a term with an empty schedule". */
export interface InstallmentSummary {
  is_installment_policy: boolean;
  payment_frequency: string;
  payment_frequency_display: string;
  number_of_payments: number | null;
  premium_paying_term_months: number | null;
  /** The end date the chosen frequency and count actually add up to. */
  projected_end_date: string | null;
  total_installments: number;
  paid_count: number;
  outstanding_count: number;
  paid_amount: string;
  outstanding_amount: string;
  next_due_date: string | null;
  next_due_amount: string | null;
  next_grace_until: string | null;
  grace_period_days: number | null;
  grace_period_source: string;
  is_past_grace: boolean;
  can_reinstate: boolean;
}

export interface InstallmentScheduleResponse {
  policy_id?: string;
  policy_number: string;
  schedule: PolicyInstallment[];
  summary: InstallmentSummary | null;
  message?: string;
  status?: string;
}

export interface RecordInstallmentPaymentRequest {
  /** Omit to settle the NEXT outstanding installment. */
  installment_id?: string;
  amount?: string;
  paid_on?: string;
  /** Required (B-02, 24 Sep): every installment payment writes a
   *  PolicyPayment receipt into Payment History, and the server refuses a
   *  payment with no mode. */
  payment_mode: string;
  payment_reference?: string;
  notes?: string;
}

/** `POST /policies/bulk-archive/`. Ids outside the caller's own book come back
 *  under `not_found` -- they are never touched and their existence is never
 *  confirmed. `unchanged` is the already-in-that-state set, so a double-click
 *  reads as a no-op rather than an error. */
export interface BulkArchiveResult {
  archived: boolean;
  updated: string[];
  unchanged: string[];
  not_found: string[];
}

export interface BulkRecalculateSkip {
  id: string;
  policy_number: string | null;
  /**
   * `not_found` | `posp_payout_completed` | `no_rate` | `policy_cancelled` |
   * `commission_withdrawn` | `error`. The last two are refusals, not failures:
   * a cancelled policy's commission was withdrawn on purpose and must not be
   * revived by a recalculation.
   */
  reason: string;
  detail: string;
}

export interface BulkRecalculateResult {
  recalculated: {
    id: string;
    policy_number: string | null;
    calculation_id: string;
    total_commission: string;
  }[];
  skipped: BulkRecalculateSkip[];
}

/** One {id, name} option, as `PolicyViewSet.filter_options` returns each of
 *  its three lists (item C5). */
export interface PolicyFilterOption {
  id: string;
  name: string;
}

export interface PolicyFilterOptions {
  insurers: PolicyFilterOption[];
  insurance_types: PolicyFilterOption[];
  products: PolicyFilterOption[];
}

export const policyService = {
  async getPolicies(
    page: number = 1,
    filters?: PolicyFilters
  ): Promise<PaginatedResponse<Policy>> {
    const params = new URLSearchParams();
    params.append('page', page.toString());

    if (filters?.search) params.append('search', filters.search);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.display_status) params.append('display_status', filters.display_status);
    if (filters?.lifecycle_status) params.append('lifecycle_status', filters.lifecycle_status);
    if (filters?.payment_status) params.append('payment_status', filters.payment_status);
    if (filters?.is_renewal !== undefined) params.append('is_renewal', String(filters.is_renewal));
    if (filters?.insurer) params.append('insurer', filters.insurer);
    if (filters?.issued !== undefined) params.append('issued', String(filters.issued));
    if (filters?.posp) params.append('posp', filters.posp);
    if (filters?.customer) params.append('customer', filters.customer);
    // `customer_group` is one PERSON — that contact AND its duplicate rows —
    // where `customer` is one row. It was missing from this list, so the
    // service accepted it and dropped it, and the Contact Details page had to
    // bypass the service with a raw `api.get` to ask the question at all
    // (see the comment on `useContactPolicies`). A filter a caller can pass
    // and the service silently discards is how a page ends up showing the
    // whole book under one person's name.
    if (filters?.customer_group) params.append('customer_group', filters.customer_group);
    // Board #279: the list hides archived policies unless asked. Omitted =
    // live records only; 'true' = archived only; 'all' = both.
    if (filters?.archived) params.append('archived', filters.archived);
    if (filters?.page_size) params.append('page_size', String(filters.page_size));

    const response = await api.get<PaginatedResponse<Policy>>(`/policies/?${params.toString()}`);
    return response.data;
  },

  async getPolicy(id: string): Promise<Policy> {
    const response = await api.get<Policy>(`/policies/${id}/`);
    return response.data;
  },

  async createPolicy(data: PolicyCreateRequest): Promise<Policy> {
    const response = await api.post<Policy>('/policies/', data);
    return response.data;
  },

  async updatePolicy(id: string, data: Partial<PolicyCreateRequest>): Promise<Policy> {
    const response = await api.patch<Policy>(`/policies/${id}/`, data);
    return response.data;
  },

  async deletePolicy(id: string): Promise<void> {
    await api.delete(`/policies/${id}/`);
  },

  // Item 4 — accounting-only flag: sets payment_status='completed' without
  // touching policy status. Broker-side roles only (server-enforced).
  async markPaymentReceived(id: string): Promise<Policy> {
    const response = await api.post<{ message: string; policy: Policy }>(`/policies/${id}/mark-payment-received/`);
    return response.data.policy;
  },

  // The remark is the point. `update_status` accepted a `notes` field and
  // discarded it for as long as it has existed, so a broker could explain a
  // status change and be told it had been saved when nothing had. It is now
  // written to the policy's activity log and comes back on the timeline.
  async updateStatus(id: string, status: string, notes?: string): Promise<Policy> {
    const response = await api.post<{ message: string; policy: Policy }>(
      `/policies/${id}/update_status/`,
      { status, notes: notes ?? '' }
    );
    return response.data.policy;
  },

  // Opens the public link. The token already exists on every policy; this is
  // the broker's explicit act of publishing it, which is the only thing the
  // public endpoint will serve on.
  //
  // `send_to_email` also MAILS the link (#259). The response then carries
  // `email_status`; a send that failed comes back as 502 with the provider's
  // reason and the link still in `public_url`, so the caller must not treat a
  // resolved promise as "the customer got it".
  async sharePolicy(
    id: string,
    options?: { send_to_email?: boolean }
  ): Promise<{
    public_token: string;
    public_url: string;
    recipient?: string;
    email_status?: string;
    message_id?: string;
  }> {
    const response = await api.post<{
      public_token: string;
      public_url: string;
      recipient?: string;
      email_status?: string;
      message_id?: string;
    }>(`/policies/${id}/share/`, options?.send_to_email ? { send_to_email: true } : {});
    return response.data;
  },

  async unsharePolicy(id: string): Promise<void> {
    await api.post(`/policies/${id}/unshare/`);
  },

  // ── Board #279: archive / restore ────────────────────────────────────────
  // NEVER a delete. An archived policy keeps every commission row, ledger
  // entry and report line it ever had; it is simply out of the default list.
  // The flag is deliberately absent from PolicyUpdateSerializer, so these
  // actions are the only door -- a PATCH carrying `is_archived` is discarded.
  async archivePolicy(id: string): Promise<Policy> {
    const response = await api.post<{ message: string; policy: Policy }>(`/policies/${id}/archive/`);
    return response.data.policy;
  },

  async unarchivePolicy(id: string): Promise<Policy> {
    const response = await api.post<{ message: string; policy: Policy }>(`/policies/${id}/unarchive/`);
    return response.data.policy;
  },

  async bulkArchivePolicies(ids: string[], archived: boolean): Promise<BulkArchiveResult> {
    const response = await api.post<BulkArchiveResult>('/policies/bulk-archive/', { ids, archived });
    return response.data;
  },

  // ── Board #281: recalculate commission for many policies at once ─────────
  // Runs the same engine calls as the single-policy recalculate. Policies the
  // batch could not move come back in `skipped` with a machine `reason` and a
  // human `detail`, so the UI can say WHY rather than reporting a flat success
  // over work that did not happen.
  async bulkRecalculateCommission(ids: string[]): Promise<BulkRecalculateResult> {
    const response = await api.post<BulkRecalculateResult>(
      '/policies/bulk-recalculate-commission/',
      { ids }
    );
    return response.data;
  },

  async getPolicyTimeline(policyId: string): Promise<PolicyTimelineEntry[]> {
    const response = await api.get<PolicyTimelineEntry[]>(`/policies/${policyId}/timeline/`);
    return response.data;
  },

  async getPolicyDocuments(policyId: string): Promise<PaginatedResponse<PolicyDocument>> {
    const response = await api.get<PaginatedResponse<PolicyDocument>>(`/policies/${policyId}/documents/`);
    return response.data;
  },

  async getPolicyPayments(policyId: string): Promise<PaginatedResponse<PolicyPayment>> {
    const response = await api.get<PaginatedResponse<PolicyPayment>>(`/policies/${policyId}/payments/`);
    return response.data;
  },

  async uploadPolicyDocument(policyId: string, formData: FormData): Promise<PolicyDocument> {
    const response = await api.post<PolicyDocument>(`/policies/${policyId}/documents/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  /**
   * Everything a renewal quote should already know, from the policy being renewed.
   *
   * `source` is either a Policy id (the Policies list "Create renewal quote"
   * button) or a Renewal id (the Renewal detail page) -- the server resolves
   * both, so the caller does not have to know which one it is holding.
   */
  async getRenewalPrefill(source: string): Promise<RenewalPrefill> {
    const response = await api.get<RenewalPrefill>('/policies/renewal-prefill/', {
      params: { source },
    });
    return response.data;
  },

  /**
   * B9 -- live (Active / Upcoming) policies already on this vehicle, within
   * the caller's own book. A prompt, never a block: the server does not refuse
   * a duplicate. `excludePolicyId` leaves the policy being edited out.
   */
  async checkVehicle(vehicleNumber: string, excludePolicyId?: string): Promise<VehicleCheckResult> {
    const response = await api.get<VehicleCheckResult>('/policies/vehicle-check/', {
      params: {
        vehicle_number: vehicleNumber,
        ...(excludePolicyId ? { exclude_policy: excludePolicyId } : {}),
      },
    });
    return response.data;
  },

  async getLastContext(): Promise<Record<string, string | null>> {
    const response = await api.get<Record<string, string | null>>('/policies/last-context/');
    return response.data;
  },

  /**
   * Item C5: distinct Insurer / Insurance Type / Product values PRESENT on
   * the caller's OWN scoped book (`apps/policies/views.py`
   * `PolicyViewSet.filter_options`, built off the same `get_queryset()` the
   * list itself uses). Used by PoliciesListPage for a customer login, whose
   * filter dropdowns must not offer the platform-wide catalogue -- see that
   * page's comment above `useCustomerPolicyFilterOptions` for why this has to
   * be a server-scoped endpoint rather than a client-side derive off the
   * (paginated) list response.
   */
  async getFilterOptions(): Promise<PolicyFilterOptions> {
    const response = await api.get<PolicyFilterOptions>('/policies/filter-options/');
    return response.data;
  },

  async lookupVehicle(
    registrationNumber: string,
    { external = false, modelId }: { external?: boolean; modelId?: string } = {},
  ): Promise<VehicleLookupResult> {
    // `external` is the only way to reach a paid vendor. The wizard calls this
    // on every debounced keystroke, so it defaults to off.
    //
    // `modelId` carries the broker's correction when our best guess was the
    // wrong car. The server honours it only if it was one of the models it
    // offered, so this cannot point the quote at an unrelated vehicle.
    const response = await api.get<VehicleLookupResult>('/policies/vehicle-lookup/', {
      params: {
        registration_number: registrationNumber,
        ...(external ? { external: 'true' } : {}),
        ...(modelId ? { model: modelId } : {}),
      },
    });
    return response.data;
  },

  /**
   * Tell the server which variant this vehicle actually is.
   *
   * Two effects, and the second is why this exists: it pins the variant on
   * this registration, AND records it against the vendor's make/model strings
   * so the next lookup of any vehicle described that way resolves on its own —
   * for every broker. The broker's judgement is the expensive part of a
   * lookup; this is what stops it being thrown away with the quote.
   */
  async confirmVehicleVariant(
    registrationNumber: string,
    variantId: string,
  ): Promise<{ learned: boolean; confirmations: number }> {
    const response = await api.post<{ learned: boolean; confirmations: number }>(
      `/vehicles/lookup/${encodeURIComponent(registrationNumber)}/variant/`,
      { variant_id: variantId },
    );
    return response.data;
  },

  /**
   * Read a registration number off a photo of a plate or an RC book.
   *
   * Returns candidates, never an answer: OCR confuses 0/O and 8/B on a phone
   * photo and a registration number has no checksum to catch it, so the user
   * picks one. Free and offline — no vendor is called.
   */
  async readRegistrationFromImage(file: File): Promise<{ candidates: string[]; found: boolean }> {
    const form = new FormData();
    form.append('image', file);
    const response = await api.post<{ candidates: string[]; found: boolean }>(
      '/vehicles/ocr/registration/',
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },

  // ── Premium-paying term ───────────────────────────────────────────────
  // Every one of these acts on the EXISTING policy: none of them creates a
  // policy and none of them changes the policy number. The responses echo
  // `policy_number` back for exactly that reason.

  /** The schedule and its summary. `summary` is null when the policy is not
   *  paid in installments. */
  async getInstallments(policyId: string): Promise<InstallmentScheduleResponse> {
    const response = await api.get<InstallmentScheduleResponse>(
      `/policies/${policyId}/installments/`
    );
    return response.data;
  },

  /** Build (or extend) the schedule from the policy's frequency and count.
   *  Safe to re-run — settled installments are never rewritten. */
  async generateInstallments(policyId: string): Promise<InstallmentScheduleResponse> {
    const response = await api.post<InstallmentScheduleResponse>(
      `/policies/${policyId}/installments/generate/`
    );
    return response.data;
  },

  /** Record one installment as paid. This is the "renewal" of a PPT policy. */
  async payInstallment(
    policyId: string,
    data: RecordInstallmentPaymentRequest
  ): Promise<InstallmentScheduleResponse> {
    const response = await api.post<InstallmentScheduleResponse>(
      `/policies/${policyId}/installments/pay/`,
      data
    );
    return response.data;
  },

  /** Bring a LAPSED installment policy back into force.
   *
   *  The server refuses while any installment is still unpaid past its grace
   *  period — record the arrears first. That refusal arrives as a 400 with a
   *  sentence naming the installments, which the caller must show rather than
   *  swallow. */
  async reinstatePolicy(policyId: string, notes?: string): Promise<Policy> {
    const response = await api.post<{ message: string; policy: Policy }>(
      `/policies/${policyId}/reinstate/`,
      { notes: notes ?? '' }
    );
    return response.data.policy;
  },

  async deletePolicyDocument(policyId: string, documentId: string): Promise<void> {
    await api.delete(`/policies/${policyId}/documents/${documentId}/`);
  },

  /** BROKER-5: `inline` asks the server for Content-Disposition: inline so the
   *  document can be rendered in-page. The server only honours it for inert
   *  types (PDF/images) and falls back to attachment otherwise. */
  async downloadPolicyDocument(policyId: string, documentId: string, inline = false): Promise<Blob> {
    const response = await api.get(
      `/policies/${policyId}/documents/${documentId}/download/${inline ? '?disposition=inline' : ''}`,
      { responseType: 'blob' }
    );
    return response.data as Blob;
  },
};

// Policy related types
/** One row of `GET /policies/:id/timeline/`. */
export interface PolicyTimelineEntry {
  event: string;
  date: string;
  details: string;
  status?: string;
  /** Present on activity-log rows: who did it, and the remark they left. */
  by?: string;
  by_role?: string;
  note?: string;
}

export interface PolicyDocument {
  id: string;
  document_type: string;
  /** #464: server label; a customer's own proof reads "Your uploaded proof", never "Payment Receipt". */
  document_type_label?: string;
  name: string;
  file_path: string;
  file_size: number | null;
  mime_type: string;
  is_verified: boolean;
  expiry_date: string | null;
  created_at: string;
}

export interface PolicyPayment {
  id: string;
  /** Mirrors PolicyPaymentSerializer (backend/apps/policies/serializers.py); there is no `policy` or `payment_mode` key. */
  payment_number: string;
  amount: string;
  /** The coarse mode stored on the row (e.g. "online"); show `payment_mode_label`, not this. */
  payment_method: string;
  /** #420: server-mapped instrument label (UPI, Card, ...); "" when unknown. */
  payment_mode_label?: string;
  payment_status: string;
  /** Omitted for customers (#391). */
  transaction_id?: string | null;
  /** Omitted for customers (#391). */
  gateway_response?: Record<string, unknown>;
  payment_date: string | null;
  created_at: string;
}
